import { useCallback, type ReactNode, useMemo, memo, useContext, lazy, Suspense } from 'react'

import ImageContentful from '@/components/ui/image-contentful'
import Video from '@/components/ui/video'
import DynamicAction, { type DynamicActionProps } from '@/components/ui/dynamic-action'
import Link from '@/components/link-component'
import RichText from '@/components/ui/rich-text'
import HubSpotForm from '@/components/ui/hubspot-form'
import Image from '@/components/ui/image'
import Lottie from '@/components/lottie'
import BarGraph from '@/components/bar-graph'
import Code from '@/components/ui/code/code'
import CodeTabs from '@/components/ui/code/code-tabs'
import Icon from '@/components/icon'
import { getExpandingBorderStyle } from '@/components/expanding-border-card'

import { cx } from '@/utils/strings'
import { renderWithBreaks } from '@/utils/render-with-breaks'
import { renderTags } from '../article-list-card'

import {
  isTypeComponentHubiloForm,
  isTypeComponentHubSpotForm,
  type TypeComponentCard,
  type TypeComponentCtaLink,
  type TypeComponentLottie,
  type TypeSectionBarGraph,
  type TypeSectionSetOfCards,
} from '@/types/ctf'
import CtaLink, { buttonStyles } from './cta-link'
import { ThemeContext } from '../theme-provider'
import { useInspectorProps } from '@/utils/live-preview'
import { isPrint } from '@/utils/print'

// Lazy-loaded so the Hubilo widget's JS + CSS only download on pages that actually render a
// form (the CSS includes a global font override that must persist once loaded — see hubilo-form.css).
const HubiloForm = lazy(() => import('@/components/ui/hubilo-form/hubilo-form'))

export type DynamicCardImage = {
  image: {
    description: string
    publicUrl: string
    title: string
  }
}

type CtaLinkEntry = TypeComponentCtaLink<'WITHOUT_UNRESOLVABLE_LINKS'>
type CardEntry = TypeComponentCard<'WITHOUT_UNRESOLVABLE_LINKS'>
type SetOfCardsEntry = TypeSectionSetOfCards<'WITHOUT_UNRESOLVABLE_LINKS'>
type LottieEntry = TypeComponentLottie<'WITHOUT_UNRESOLVABLE_LINKS'>
type BarGraphEntry = TypeSectionBarGraph<'WITHOUT_UNRESOLVABLE_LINKS'>

export const cardBgColors: Record<string, string> = {
  lightGray: 'Light Gray',
  white: 'White',
  indigoBlue: 'Indigo Blue',
  primaryBlue: 'Primary Blue',
}

export type CardProps = Omit<CardEntry['fields'], 'actions' | 'subtext'> &
  Omit<SetOfCardsEntry['fields'], 'actions'> & {
    entryId?: string
    className?: string
    subtext?: CardEntry['fields']['subtext'] | string
    actions?: DynamicActionProps[]
    columnCount?: number
    isHero: boolean
    tags?: string[]
    sectionHasHeadline?: boolean
    backgroundStyle?: string
    enableCardLinkForLabeledCta?: boolean
  }

function Card({
  entryId,
  className,
  actions,
  asset,
  cardIconLocation,
  cardIconSize = '1/3',
  columnCount = 1,
  headline,
  image,
  isHero = false,
  subtext,
  subtextAlignment,
  tagline,
  tags,
  imageType,
  showHeroImageOnMobile = false,
  heroLayout,
  imageDarkMode,
  imageBackgroundColor,
  sectionHasHeadline = false,
  backgroundStyle,
  cardBackgroundColor,
  gradientCurves,
  stats,
  assetImageBelowText,
  centerCardTextAndActions,
  enableCardLinkForLabeledCta = false,
}: CardProps) {
  const inspectorProps = useInspectorProps({ entryId, restrictedTypes: ['sectionSetOfCards', 'componentCard'] })
  const pageInspectorProps = useInspectorProps({ entryId, restrictedTypes: ['pages'] })
  const { currentTheme } = useContext(ThemeContext)
  const isDarkMode = currentTheme === 'dark'
  const isPrintView = isPrint()

  // Memoize calculated values to prevent re-renders
  const soleAction: any = useMemo(
    () =>
      actions?.length === 1 && actions[0]?.type !== 'componentHubspotCta'
        ? (actions[0] as CtaLinkEntry & { to: string })
        : null,
    [actions]
  )

  const soleActionLink: string | null = useMemo(
    () =>
      (soleAction as any)?.link?.sys?.contentType?.sys?.id === 'componentLink'
        ? (soleAction as any)?.link?.fields?.url
        : (soleAction as { to: string })?.to,
    [soleAction]
  )

  const soleActionLabel: string = useMemo(
    () => (soleAction?.label || soleAction?.fields?.label || '').trim(),
    [soleAction]
  )

  // Whole card is a link if:
  //  - There's only one action
  //  - The action is a link (not a HubSpot CTA)
  //  - The card is not a hero card
  //  - The card is not a single-column card
  //  - The action has no label (so the link is not duplicated in the card body), unless
  //    `enableCardLinkForLabeledCta` is set — then the labeled CTA renders as a button
  //    inside the card link instead of a nested anchor
  const isCardLink = useMemo(
    () =>
      !!(
        soleAction &&
        soleActionLink &&
        !isHero &&
        columnCount !== 1 &&
        (!soleActionLabel || enableCardLinkForLabeledCta)
      ),
    [soleAction, soleActionLink, isHero, columnCount, soleActionLabel, enableCardLinkForLabeledCta]
  )
  const hasCardTextContent = headline || subtext || (actions && actions.length > 0)

  const isSvg = useMemo(
    () =>
      (image?.fields?.file?.contentType || (asset as any)?.fields?.image?.fields?.file?.contentType) ===
      'image/svg+xml',
    [image?.fields?.file?.contentType, (asset as any)?.fields?.image?.fields?.file?.contentType]
  )

  // Memoize computed imageType to prevent recalculation
  const computedImageType = useMemo(() => {
    let type = imageType
    type ??= isSvg ? 'Spot' : 'Image'
    if ((isSvg && tagline === 'Resource') || (isSvg && tagline === 'Blog') || (isSvg && isHero)) {
      type = 'Image'
    }
    return type
  }, [imageType, isSvg, tagline, isHero])

  // Memoize form type checks to prevent recalculation
  const isHubSpotForm = useMemo(() => asset && isTypeComponentHubSpotForm(asset), [asset])
  const isHubiloForm = useMemo(() => asset && isTypeComponentHubiloForm(asset), [asset])

  const LinkWrap = useCallback(
    ({
      children,
      to,
      plausibleEventName = '',
    }: {
      children: ReactNode
      to?: string | null | false
      plausibleEventName: string
    }) => (
      <>
        {to ? (
          <Link
            className={`flex w-full text-secondary-800 dark:text-secondary-50 ${plausibleEventName?.trim()}`}
            to={to}
          >
            {children}
          </Link>
        ) : (
          children
        )}
      </>
    ),
    []
  )

  const HeroHeadline = tagline ? 'div' : sectionHasHeadline ? 'h2' : 'h1'
  const Tagline = useCallback(() => {
    const Wrapper = isHero ? (sectionHasHeadline ? 'h2' : 'h1') : 'div'
    return (
      <Wrapper
        data-testid="card-tagline"
        className={cx('text-eyebrow uppercase pt-4 @3xl:pt-0', isHero && 'mb-0 header-gradient-reset')}
        {...inspectorProps({ fieldId: 'tagline' })}
      >
        {tagline}
      </Wrapper>
    )
  }, [tagline, isHero, sectionHasHeadline])

  const LinkRenderer = useCallback((node: { data: { uri: string } }, children: ReactNode) => {
    return (
      <Link className="text-primaryBlue" to={node.data.uri}>
        {children}
      </Link>
    )
  }, [])

  // Memoize image props to prevent object recreation on every render
  const imageProps = useMemo(() => {
    const imageFields = image?.fields
    const loadingValue: 'eager' | 'lazy' = isHero ? 'eager' : 'lazy'

    if (!imageFields) {
      return {
        darkModeImage: imageDarkMode?.fields,
        loading: loadingValue,
      }
    }
    return {
      ...imageFields,
      darkModeImage: imageDarkMode?.fields,
      loading: loadingValue,
    }
  }, [image?.fields, imageDarkMode?.fields, isHero])

  // Memoize the image rendering function to prevent unnecessary re-renders
  const renderImageContentful = useCallback(
    () => (
      <ImageContentful
        {...imageProps}
        className={cx(
          (!computedImageType || computedImageType === 'Image') && 'w-full',
          computedImageType === 'Spot' && `w-[160px]! @3xl:w-full!`,
          computedImageType === 'Icon' && `w-[60px]!  @3xl:w-full! mb-2 @3xl:mb-0`,
          computedImageType === 'Image' && cx(cardIconSize === 'Unconstrained' ? '@3xl:h-full' : `rounded`),
          !headline && !subtext && !asset && !actions && `max-w-[1000px] mx-auto`,
          heroLayout === 'Vertical' && `max-w-[1000px] mx-auto rounded-lg`,
          imageBackgroundColor === 'White' && `dark:bg-white!`,
          imageBackgroundColor === 'Primary Blue (Blend Mode)' &&
            '[&_img]:relative [&_img]:z-[1] before:absolute before:inset-0 before:z-[1] before:bg-[linear-gradient(115deg,var(--color-blue-500)_25%,var(--color-blue-700)_78%)] before:size-full before:mix-blend-plus-lighter dark:before:mix-blend-color-dodge dark:before:opacity-50',
          cardIconSize === '1/2 (Bleed)' &&
            heroLayout === 'Horizontal' &&
            cx(
              `@5xl:w-[50vw]! @5xl:max-h-[70vh] @5xl:[&_img]:max-h-[70vh] @5xl:max-w-none @5xl:[&_img]:object-contain`,
              cardIconLocation === 'Right' && '@5xl:[&_img]:object-left -translate-x-1 2xl:-translate-x-2',
              cardIconLocation === 'Left' && '@5xl:[&_img]:object-right translate-x-1 2xl:translate-x-2'
            )
        )}
        setAspectRatio={!(cardIconSize === '1/2 (Bleed)' && heroLayout === 'Horizontal')}
        {...inspectorProps({ fieldId: isDarkMode ? 'imageDarkMode' : 'image' })}
      />
    ),
    [imageProps, computedImageType, headline, subtext, asset, actions, heroLayout, isSvg, imageBackgroundColor]
  )

  const assetBlock = (image || asset) && (
    <div
      data-testid="card-asset"
      className={cx(
        asset?.sys?.contentType?.sys?.id === 'componentCode' && 'lg:[&_.bw-code-container]:max-h-[38rem]',
        (tagline === 'Resource' || tagline === 'Blog') && 'w-full @3xl:w-1/3',
        computedImageType === 'Image' &&
          cx(
            columnCount > 1 && 'mb-2 @3xl:mb-0',
            !subtext && !headline && '@3xl:w-full!',
            cardIconSize === 'Unconstrained' &&
              !isHero &&
              '@3xl:h-full @3xl:basis-[150%] @3xl:max-w-1/2 @3xl:[&_img]:h-full @3xl:[&_img]:object-cover @3xl:[&_img]:aspect-[2.5]'
          ),
        heroLayout === 'Horizontal' &&
          cx(
            asset?.sys?.contentType?.sys?.id === 'componentCodeTabs' && 'w-full flex-[1 0 auto]',
            (cardIconSize === '1/3' || cardIconSize === '1/4') && '@3xl:w-1/3',
            (cardIconSize === '1/2' || cardIconSize === 'Full') && '@3xl:w-1/2',
            cardIconSize === '1/2 (Bleed)' && '@3xl:w-[calc(50%+0.5rem)]',
            cardIconSize === '2/3' && '@3xl:w-2/3',
            hasCardTextContent &&
              cx(
                cardIconLocation === 'Right' &&
                  (cardIconSize === '1/2 (Bleed)' ? '@3xl:pl-4 lg:@3xl:pl-8' : '@3xl:pl-8 lg:@3xl:pl-16'),
                cardIconLocation === 'Left' && '@3xl:pr-8'
              )
          ),
        isHero &&
          cx(
            !showHeroImageOnMobile && !isHubSpotForm && !isHubiloForm && 'hidden @3xl:sm:block',
            (isHubSpotForm || isHubiloForm) && '@3xl:self-start block',
            heroLayout === 'Horizontal' &&
              isHubSpotForm &&
              cx(
                'bg-white shadow-md dark:bg-secondary-800 rounded-lg p-5 lg:p-10 border mb-5 lg:mb-0 lg:@3xl:mr-16 @3xl:pr-5! lg:@3xl:pr-10!',
                cardIconLocation === 'Left' && '@3xl:mr-8 lg:@3xl:mr-16',
                cardIconLocation === 'Right' && '@3xl:ml-8 lg:@3xl:ml-16'
              ),
            heroLayout === 'Vertical' &&
              cx(
                isHubSpotForm &&
                  cx(
                    'hs-form-w-gradient-background max-w-[580px] mx-auto',
                    backgroundStyle === 'Midnight' && 'hs-form-w-light-gradient-background'
                  ),
                !isHubSpotForm && 'p-6'
              )
          ),
        assetImageBelowText && cardIconSize === 'Unconstrained' && 'mb-0! mt-10'
      )}
    >
      {image ? (
        (() => {
          const shouldRenderWithWrapper = (isSvg && tagline === 'Resource') || (isSvg && tagline === 'Blog')

          return shouldRenderWithWrapper ? (
            <div className=" bg-primaryBlue [&_*]:aspect-video w-full rounded">{renderImageContentful()}</div>
          ) : (
            renderImageContentful()
          )
        })()
      ) : (
        <>
          {isHubSpotForm && (
            <HubSpotForm
              className={isHero && heroLayout === 'Horizontal' ? 'hubspot-hero-form-horizontal' : ''}
              {...(asset as any)?.fields}
            />
          )}
          {isHubiloForm && (
            <Suspense fallback={<div className="min-h-[10rem]" />}>
              <HubiloForm {...(asset as any)?.fields} id={asset?.sys?.id} />
            </Suspense>
          )}

          {asset?.sys?.contentType?.sys?.id === 'componentVideo' && (
            <Video className="rounded overflow-hidden" {...(asset as any)} />
          )}

          {asset?.sys?.contentType?.sys?.id === 'resourceImage' && (
            <Image
              className={cx(
                'w-full',
                isSvg &&
                  cx(`[&_svg]:h-full [&_svg]:w-full`, !headline && !subtext && !asset && !actions && `max-w-[1000px]`),
                computedImageType === 'Spot' && `w-[160px]! @3xl:w-full! rounded-lg`,
                computedImageType === 'Icon' && `w-[60px]!  @3xl:w-full! rounded-lg`
              )}
              loading={isHero ? 'eager' : 'lazy'}
              {...(asset?.fields as any)}
              darkModeImage={asset.imageDarkMode?.fields}
            />
          )}
          {asset?.sys?.contentType?.sys?.id === 'componentLottie' && <Lottie {...(asset as LottieEntry)?.fields} />}
          {asset?.sys?.contentType?.sys?.id === 'sectionBarGraph' && <BarGraph {...(asset as BarGraphEntry)?.fields} />}
          {asset?.sys?.contentType?.sys?.id === 'componentCode' && <Code {...(asset as any)?.fields} />}
          {asset?.sys?.contentType?.sys?.id === 'componentCodeTabs' && <CodeTabs {...(asset as any)?.fields} />}
          {(asset as any)?.sys?.contentType?.sys?.id === 'componentIcon' && (
            <div className="flex size-12 items-center justify-center bg-primaryBlue rounded mb-3">
              <Icon icon={(asset as any)?.fields.unicode} className="text-3xl text-white" />
            </div>
          )}
        </>
      )}
    </div>
  )

  const contentBlock = hasCardTextContent && (
    <div
      data-testid="card-content"
      className={cx(
        heroLayout === 'Horizontal' &&
          cx(
            (cardIconSize === '1/3' || cardIconSize === '1/4') && `@3xl:w-2/3`,
            (cardIconSize === '1/2' || cardIconSize === '1/2 (Bleed)' || cardIconSize === 'Full') && `@3xl:w-1/2`,
            cardIconSize === '2/3' && `@3xl:w-1/3`
          ),
        !headline && !subtext && !asset && !actions && `hidden`,
        asset && !headline && !subtext && !image && !actions && `hidden`,
        !asset && !image && isHero && `text-center xl:w-4/5 m-auto`,
        isHero && heroLayout === 'Vertical' && `text-center w-full px-6 pt-16 lg:pb-10`,
        // isHero && heroLayout === 'Horizontal' && `py-3 lg:py-6`,
        (tagline === 'Resource' || tagline === 'Blog') && 'w-full @3xl:w-2/3',
        centerCardTextAndActions && 'text-center @3xl:text-left',
        cardIconSize === 'Unconstrained' &&
          !isHero &&
          !assetImageBelowText &&
          cx(
            computedImageType === 'Image' &&
              cx(
                columnCount === 1 && cx('px-9', tagline ? 'py-8' : 'pt-5 pb-8'),
                columnCount >= 2 && cx('px-9 pt-2 pb-8', tagline ? '@3xl:pt-8' : '@3xl:pt-4.5')
              )
          ),
        assetImageBelowText && 'px-5 lg:px-9 py-5 lg:py-10  @3xl:px-9 @3xl:py-5'
      )}
    >
      {tagline && <Tagline />}
      {headline && (
        <div data-testid="card-headline" {...inspectorProps({ fieldId: 'headline' })}>
          {isHero ? (
            <HeroHeadline
              className={cx(
                'mb-4 hyphens-auto firefox:hyphens-manual text-balance header-gradient',
                sectionHasHeadline ? 'h2' : 'card-hero-h1 h1',
                heroLayout === 'Horizontal' && isHubSpotForm && `h2`
              )}
            >
              {renderWithBreaks(headline)}
            </HeroHeadline>
          ) : (
            <h3
              className={cx(
                !sectionHasHeadline && columnCount === 1 && 'h3 header-gradient text-balance',
                sectionHasHeadline && columnCount === 1 && 'h4',
                columnCount === 2 && 'h4',
                columnCount === 3 && 'h5',
                columnCount >= 4 && 'h6'
              )}
            >
              {renderWithBreaks(headline)}
            </h3>
          )}
        </div>
      )}
      {tags && renderTags(tags)}
      <div
        data-testid="card-subtext"
        className={cx(
          '[&_h2]:h3 [&_p:last-of-type]:mb-0 pt-2 pb-2',
          subtextAlignment === 'Center' && 'text-center',
          subtextAlignment === 'Right' && 'text-right',
          isHero && heroLayout === 'Vertical' && `m-auto @3xl:max-w-2/3`,
          isCardLink && !!tagline?.length && 'mb-16 @3xl:mb-6',
          !headline && 'pt-5 @3xl:pt-0',
          isPrintView && 'pt-0!'
        )}
        {...inspectorProps({ fieldId: 'subtext' })}
      >
        {typeof subtext === 'string' && <p>{subtext}</p>} {/* Blog Page/Landing Page cards pass a string */}
        {typeof subtext === 'object' && (
          <RichText
            body={subtext}
            a={LinkRenderer}
            renderImage={(ref) => <Image image={ref} showCaption={false} loading={isHero ? 'eager' : 'lazy'} />}
          />
        )}
      </div>
      {isCardLink && tagline?.length && !soleActionLabel && (
        <div
          className={cx(
            'border-t border-secondary-100 absolute bottom-0 left-6 right-6 py-5 text-right @3xl:relative @3xl:left-auto @3xl:right-auto'
          )}
        >
          <CtaLink asButton style="Arrow Link" className="font-bold">
            Read more
          </CtaLink>
        </div>
      )}
      {(!isCardLink || !!soleActionLabel) && actions && (
        <div
          className={cx(
            'flex flex-wrap gap-4 items-center mt-4',
            ((!asset && !image && isHero) || heroLayout === 'Vertical') && `justify-center`,
            centerCardTextAndActions && 'justify-center @3xl:justify-start'
          )}
          {...inspectorProps({ fieldId: 'actions' })}
        >
          {actions.map((action) => {
            return (
              <DynamicAction
                key={action.id}
                {...action}
                asButton={isCardLink}
                data-testid="card-cta"
                className={cx(
                  // Special cases for Card Background Colors
                  action.style === buttonStyles.btnSolid &&
                    cardBackgroundColor === cardBgColors.primaryBlue &&
                    'cta-solid-secondary',
                  [buttonStyles.btnOutlined, buttonStyles.btnOutlinedSecondary].includes(action.style) &&
                    [cardBgColors.primaryBlue, cardBgColors.indigoBlue].includes(cardBackgroundColor as string) &&
                    'cta-outline-tertiary',
                  [buttonStyles.btnArrow, buttonStyles.btnPlain, buttonStyles.btnText].includes(action.style) &&
                    [cardBgColors.primaryBlue, cardBgColors.indigoBlue].includes(cardBackgroundColor as string) &&
                    'text-white hover:text-accent-teal!'
                )}
              />
            )
          })}
        </div>
      )}
      {stats && (
        <div className="flex gap-x-7.5 gap-y-3.5 pt-12 flex-wrap">
          {stats.map((stat) => {
            if (!stat) return null
            return (
              <div key={stat.sys.id} className={cx('flex items-center shrink-0 gap-1.5')}>
                {stat.fields.icon && (
                  <Icon icon={stat.fields.icon.fields.unicode} className="text-[1.375rem] text-primaryBlue" />
                )}
                <div className="text-[1.125rem] pt-0.5">
                  <span className="font-bold text-primaryBlue">{stat.fields.statisticNumber}&nbsp;</span>
                  <span className="text-secondary-600 dark:text-secondary-400 whitespace-nowrap">
                    {stat.fields.statisticText}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )

  return (
    <div
      className={cx(
        '@container flex',
        className,
        isHero && heroLayout === 'Horizontal' && `pt-10`,
        isCardLink && !isPrintView && getExpandingBorderStyle({ borderSize: columnCount > 2 ? 'sm' : 'md' })
      )}
      {...pageInspectorProps({ fieldId: 'headline' })}
    >
      <LinkWrap to={isCardLink && soleActionLink} plausibleEventName={soleAction?.plausibleEventName}>
        <div
          className={cx(
            '@3xl:flex w-full',
            !(cardIconSize === 'Unconstrained' && computedImageType === 'Image') && !isHero && 'p-5',
            (columnCount >= 2 || cardBackgroundColor) &&
              'overflow-hidden rounded-lg border border-black/10 dark:border-white/10',
            cardBackgroundColor === cardBgColors.lightGray &&
              'bg-bw-gray-teal text-secondary-800 dark:bg-gray-700 dark:text-secondary-50',
            cardBackgroundColor === cardBgColors.white &&
              'bg-white text-secondary-800 dark:bg-secondary-800 dark:text-secondary-50',
            cardBackgroundColor === cardBgColors.indigoBlue && 'bg-bw-deep-blue text-white dark:bg-primary-900/20',
            cardBackgroundColor === cardBgColors.primaryBlue && 'bg-primary-500 text-white',
            !isHero &&
              !cardBackgroundColor &&
              columnCount >= 2 &&
              cx(
                backgroundStyle || isPrintView
                  ? 'bg-white dark:bg-secondary-800'
                  : 'bg-bw-gray-teal dark:bg-secondary-800'
              ),
            cardIconLocation === 'Right' &&
              heroLayout === 'Horizontal' &&
              'flex flex-col gap-4 @3xl:gap-0 @3xl:flex-row-reverse @3xl:space-x-reverse lg:@3xl:space-x-reverse',
            isHero && cardIconLocation === 'Right' && heroLayout === 'Horizontal' && 'flex-colomn-reverse',
            (isHubSpotForm || isHubiloForm) && cardIconLocation === 'Right' && 'flex-col-reverse',
            heroLayout === 'Horizontal' &&
              cx('@3xl:flex-row', !isHubSpotForm && 'items-center', isHubSpotForm && `items-start`),
            heroLayout === 'Vertical' && `flex flex-col-reverse`,
            isPrintView && 'border-0 px-0!',
            gradientCurves === 'Gradient Curves 1' &&
              !isPrintView &&
              'relative z-3 card-gradient-curve-1 before:content-[""] before:absolute before:inset-0 before:-z-1 before:bg-cover before:bg-center before:bg-no-repeat',
            isCardLink && 'z-1 hover:border-transparent',
            assetImageBelowText && 'flex flex-col @3xl:flex-row justify-between @3xl:justify-start'
          )}
          data-testid="card-container"
        >
          {/* DOM order follows visual order for a11y: content precedes asset when assetImageBelowText */}
          {assetImageBelowText ? (
            <>
              {contentBlock}
              {assetBlock}
            </>
          ) : (
            <>
              {assetBlock}
              {contentBlock}
            </>
          )}
        </div>
      </LinkWrap>
    </div>
  )
}

// Memoize the Card component to prevent unnecessary re-renders
export default memo(Card)
